import React from 'react';

import {Dialog, AppBar, Toolbar, Box} from '@mui/material';

import {CxIconButton} from "./CxButton";
import {CxSpacer} from "./CxTypography"
import {Report} from "../domain/Report"

// ------------------------------------------------------------------------------------------------
// Report styles and constants
// ------------------------------------------------------------------------------------------------

const logoUrl = "https://marrer.work/mbc/logo.png";

const tableStyle = `
        border-collapse: collapse;
        width: 100%;
        font-size: 12px;
`;

const thStyle = `
        border: 1px solid #ccc;
        padding: 4px 6px;
        text-align: left;
        vertical-align: top;
        background-color: #f0f0f0;
`;

const tdStyle = `
        border: 1px solid #ccc;
        padding: 4px 6px;
        text-align: left;
        vertical-align: top;
`;

const containerStyle = `
        padding: 12px;
        font-family: Arial, sans-serif;
`;

const titleStyle = `
    font-size: 30px;
`;

const infoStyle = `
    color: #d65d51;
    font-size: 14px; 
    font-weight: bold;
    margin: 0;
`;

const subtitleStyle = `
    font-size: 18px;
    margin: 0;
    margin-bottom: 6px;
`;

// ------------------------------------------------------------------------------------------------
// Render HTML parts
// ------------------------------------------------------------------------------------------------

function renderHeader(title, info = null) {
    return `
        <div style="${containerStyle}">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem;">
                <div>
                    <h1 style="${titleStyle}">${title}</h1>
                    ${info ? `<p style="${infoStyle}">${info}</p>` : ''}
                </div>
                <img src="${logoUrl}" alt="Logo" style="height: 60px;" />
            </div>
        </div>
    `;
}

function renderTable(subtitle, columns, rows) {
    const hasRows = rows && rows.length > 0;

    return `
        <div style="${containerStyle}">
            ${subtitle ? `
                <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem;">
                    <h2 style="${subtitleStyle}">${subtitle}</h2>
                </div>
            ` : ''}

            ${hasRows ? `
                <table style="${tableStyle}">
                    <thead>
                        <tr>
                            ${columns.map(col => `<th style="${thStyle}">${col.headerName}</th>`).join('')}
                        </tr>
                    </thead>
                    <tbody>
                        ${rows.map(row => `
                            <tr>
                                ${columns.map(col => `<td style="${tdStyle}">${row[col.field] ?? ''}</td>`).join('')}
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
            ` : `<p>Keine Einträge vorhanden.</p>`}
        </div>
    `;
}

// ------------------------------------------------------------------------------------------------
// Report dialog
// ------------------------------------------------------------------------------------------------

export function CxReportDialog({ reportType, title, rows, open, onClose, info = null}) {
    const name = Report.getName(reportType);
    const columns = Report.getColumns(reportType);

    const handlePrint = () => {
        const printWindow = window.open('', '_blank');
        if (!printWindow) return;

        const html = `
            <html>
                <head><title>${name}</title></head>
                <body>
                    ${renderHeader(title, info)}
                    ${renderTable(null, columns, rows)}
                    <script>
                        window.onload = function() {
                            window.print();
                        };
                    </script>
                </body>
            </html>
        `;

        printWindow.document.open();
        printWindow.document.write(html);
        printWindow.document.close();
    };

    const handleCsvDownload = () => {
        const csv = [
            columns.map(col => `"${col.headerName}"`).join(',')
        ];
        rows.forEach(row => {
            csv.push(
                columns.map(col => `"${row[col.field] ?? ''}"`).join(',')
            );
        });
        const blob = new Blob([csv.join('\n')], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${name}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <Dialog fullScreen open={open} onClose={onClose}>
            <AppBar position="sticky" color="default">
                <Toolbar>
                    <CxIconButton iconName="print"    label="Print" onClick={handlePrint} sx={{ ml:1, mr:1 }} />
                    <CxIconButton iconName="download" label="CSV" onClick={handleCsvDownload} sx={{ ml:1, mr:1 }} />
                    <CxSpacer />
                    <CxIconButton iconName="close" label="Schliessen" onClick={onClose} sx={{ ml:1, mr:1 }} />
                </Toolbar>
            </AppBar>
            <Box p={2}>
                <div dangerouslySetInnerHTML={{ __html: renderHeader(title, info) }} />
                <div dangerouslySetInnerHTML={{ __html: renderTable(null, columns, rows) }} />
            </Box>
        </Dialog>
    );
}

// ------------------------------------------------------------------------------------------------
// Grouped Report dialog
// ------------------------------------------------------------------------------------------------

export function CxGroupReportDialog({ reportType, title, rows, groupBy, open, onClose, info = null, }) {
    const name = Report.getName(reportType);
    const columns = Report.getColumns(reportType);

    // Group rows by groupBy field
    const groupMap = new Map();
    rows.forEach(row => {
        const groupValue = row[groupBy] ?? "_keine Gruppe_";
        if (!groupMap.has(groupValue)) {
            groupMap.set(groupValue, []);
        }
        groupMap.get(groupValue).push(row);
    });

    // Table columns without the groupBy field
    const filteredColumns = columns.filter(col => col.field !== groupBy);

    const handlePrint = () => {
        const printWindow = window.open('', '_blank');
        if (!printWindow) return;


        const html = `
            <html>
                <head><title>${name}</title></head>
                <body>
                    ${renderHeader(title, info)}
                    ${Array.from(groupMap.entries()).map(([group, groupRows]) =>
                        renderTable(group, filteredColumns, groupRows)
                    ).join('')}
                    <script>
                        window.onload = function() {
                            window.print();
                        };
                    </script>
                </body>
            </html>
        `;

        printWindow.document.open();
        printWindow.document.write(html);
        printWindow.document.close();
    };

    const handleCsvDownload = () => {
        const csv = [
            columns.map(col => `"${col.headerName}"`).join(',')
        ];
        rows.forEach(row => {
            csv.push(
                columns.map(col => `"${row[col.field] ?? ''}"`).join(',')
            );
        });
        const blob = new Blob([csv.join('\n')], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${name}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <Dialog fullScreen open={open} onClose={onClose}>
            <AppBar position="sticky" color="default">
                <Toolbar>
                    <CxIconButton iconName="print" label="Print" onClick={handlePrint} sx={{ ml: 1, mr: 1 }} />
                    <CxIconButton iconName="download" label="CSV" onClick={handleCsvDownload} sx={{ ml: 1, mr: 1 }} />
                    <CxSpacer />
                    <CxIconButton iconName="close" label="Schliessen" onClick={onClose} sx={{ ml: 1, mr: 1 }} />
                </Toolbar>
            </AppBar>
            <Box p={2}>
                <div dangerouslySetInnerHTML={{ __html: renderHeader(title, info) }} />
                {Array.from(groupMap.entries()).map(([group, groupRows]) => (
                    <div
                        key={group}
                        dangerouslySetInnerHTML={{
                            __html: renderTable(group, filteredColumns, groupRows),
                        }}
                    />
                ))}
            </Box>
        </Dialog>
    );
}